/**
 * Persistence for in-progress admin forms, so a page refresh does not discard
 * work.
 *
 * Field values are small and go in localStorage. Pending image blobs do not —
 * uploads are capped at 5 MB each and localStorage is ~5 MB total, so they live
 * in IndexedDB instead.
 *
 * Every function degrades to a no-op rather than throwing: losing a draft is an
 * inconvenience, but breaking the form because storage is full or blocked
 * (private browsing, disabled cookies) would be worse.
 */

const DB_NAME = 'doctape-admin';
const DB_VERSION = 1;
const BLOB_STORE = 'pendingImages';

// ── IndexedDB: pending image blobs ─────────────────────────────────────────────

const openDb = () =>
    new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB unavailable'));
            return;
        }
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(BLOB_STORE)) {
                db.createObjectStore(BLOB_STORE);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

const runRequest = (store, method, ...args) =>
    new Promise((resolve, reject) => {
        const request = store[method](...args);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

const withStore = async (mode, fn) => {
    const db = await openDb();
    try {
        const transaction = db.transaction(BLOB_STORE, mode);
        const result = await fn(transaction.objectStore(BLOB_STORE));
        await new Promise((resolve, reject) => {
            transaction.oncomplete = resolve;
            transaction.onerror = () => reject(transaction.error);
            transaction.onabort = () => reject(transaction.error);
        });
        return result;
    } finally {
        db.close();
    }
};

export const putPendingBlob = async (id, blob) => {
    try {
        await withStore('readwrite', (store) => runRequest(store, 'put', blob, id));
        return true;
    } catch {
        return false;
    }
};

export const getPendingBlob = async (id) => {
    try {
        return await withStore('readonly', (store) => runRequest(store, 'get', id));
    } catch {
        return undefined;
    }
};

export const deletePendingBlobs = async (ids = []) => {
    if (!ids.length) return;
    try {
        await withStore('readwrite', (store) =>
            Promise.all(ids.map((id) => runRequest(store, 'delete', id)))
        );
    } catch {
        /* nothing worth surfacing */
    }
};

// ── localStorage: form field values ────────────────────────────────────────────

const draftKey = (scope) => `doctape:draft:${scope}`;

export const saveDraft = (scope, draft) => {
    try {
        localStorage.setItem(draftKey(scope), JSON.stringify(draft));
        return true;
    } catch {
        return false; // quota exceeded or storage blocked
    }
};

export const loadDraft = (scope) => {
    try {
        const raw = localStorage.getItem(draftKey(scope));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const clearDraft = async (scope, blobIds = []) => {
    try {
        localStorage.removeItem(draftKey(scope));
    } catch {
        /* ignore */
    }
    await deletePendingBlobs(blobIds);
};
